// =============================================================================
// renderSlotted — shared renderer for sentences carrying v5.1 placeholder
// slots ("[firstName]" bare slots and "[key: description]" descriptive
// slots).
//
// Lives in its own file because BOTH the calibration deck and the phrase
// bank preview render annotated sentences; duplicating the regex / pill
// markup in two places would drift. Single source of truth here.
//
// Visual contract:
//   • [firstName], [businessName], etc. → muted italic pill, no tooltip
//     (the user knows what these are at a glance)
//   • [key: human description]          → italic «description» with the
//     KEY in the title tooltip so power-users can hover and see the
//     semantic slot name
// =============================================================================

import React from "react";

// Same length budget as the backend sanitizer's SLOT_PATTERN (~80 chars
// to accommodate descriptive slots). Anything longer is treated as plain
// text — almost certainly NOT a placeholder.
const SLOT_RE = /\[([^\]\n]{1,80})\]/g;

const splitKeyDescription = (raw) => {
  const idx = raw.indexOf(":");
  if (idx === -1) return { key: raw.trim(), description: "" };
  return {
    key: raw.slice(0, idx).trim(),
    description: raw.slice(idx + 1).trim(),
  };
};

// Renders text with slot placeholders highlighted. Returns an array of
// React fragments so the caller can drop the result inside a <span> or
// <p> without adding extra wrapping.
export const renderSlotted = (text) => {
  if (!text || typeof text !== "string") return [text];
  const parts = [];
  let lastIdx = 0;
  let match;
  SLOT_RE.lastIndex = 0;
  while ((match = SLOT_RE.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(text.slice(lastIdx, match.index));
    }
    const { key, description } = splitKeyDescription(match[1]);
    if (description) {
      parts.push(
        <span
          key={`slot-${match.index}`}
          className="cca-slot cca-slot--descriptive"
          title={`Semantic slot: ${key} → filled at send-time with real campaign content`}
        >
          «{description}»
        </span>,
      );
    } else {
      parts.push(
        <span
          key={`slot-${match.index}`}
          className="cca-slot cca-slot--bare"
          title="Slot — filled at send-time with recipient data"
        >
          {key}
        </span>,
      );
    }
    lastIdx = match.index + match[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
};
